import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createUseStyles } from "react-jss";

export const CARD_SLIDE_MS = 240;
const SLIDE_OFFSET_PX = 24;

export type SlidePhase = "idle" | "exit" | "enter";
export type SlideDirection = "forward" | "backward";

type QueuedNavigation = {
  direction: SlideDirection | (() => SlideDirection);
  navigate: () => void;
};

const resolveDirection = (direction: SlideDirection | (() => SlideDirection)) =>
  typeof direction === "function" ? direction() : direction;

const useStyles = createUseStyles({
  stage: {
    position: "relative",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    overflow: "hidden"
  },
  slide: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    willChange: "transform, opacity"
  },
  idle: {
    transform: "translateX(0)",
    opacity: 1
  },
  exitLeft: {
    transform: `translateX(-${SLIDE_OFFSET_PX}px)`,
    opacity: 0
  },
  exitRight: {
    transform: `translateX(${SLIDE_OFFSET_PX}px)`,
    opacity: 0
  },
  enterFromRight: {
    transform: `translateX(${SLIDE_OFFSET_PX}px)`,
    opacity: 0
  },
  enterFromLeft: {
    transform: `translateX(-${SLIDE_OFFSET_PX}px)`,
    opacity: 0
  }
});

export const useStudyCardSlide = (
  goToNextCard: () => void,
  goToPreviousCard: () => void,
  canGoNext: boolean,
  canGoPrevious: boolean
) => {
  const [slidePhase, setSlidePhase] = useState<SlidePhase>("idle");
  const [slideDirection, setSlideDirection] = useState<SlideDirection>("forward");
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queueRef = useRef<QueuedNavigation[]>([]);
  const isProcessingRef = useRef(false);

  const processQueue = useCallback(() => {
    if (isProcessingRef.current || queueRef.current.length === 0) {
      return;
    }

    const next = queueRef.current.shift();
    if (!next) {
      return;
    }

    isProcessingRef.current = true;
    setSlideDirection(resolveDirection(next.direction));
    setTransitionEnabled(true);
    setSlidePhase("exit");

    timeoutRef.current = setTimeout(() => {
      setTransitionEnabled(false);
      next.navigate();
      setSlidePhase("enter");

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitionEnabled(true);
          setSlidePhase("idle");
          isProcessingRef.current = false;
          timeoutRef.current = null;
          processQueue();
        });
      });
    }, CARD_SLIDE_MS);
  }, []);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      queueRef.current = [];
      isProcessingRef.current = false;
    },
    []
  );

  const animateNavigation = useCallback(
    (direction: SlideDirection | (() => SlideDirection), navigate: () => void) => {
      queueRef.current.push({ direction, navigate });
      processQueue();
    },
    [processQueue]
  );

  const animateToNextCard = useCallback(() => {
    if (!canGoNext) {
      return;
    }
    animateNavigation("forward", goToNextCard);
  }, [animateNavigation, canGoNext, goToNextCard]);

  const animateToPreviousCard = useCallback(() => {
    if (!canGoPrevious) {
      return;
    }
    animateNavigation("backward", goToPreviousCard);
  }, [animateNavigation, canGoPrevious, goToPreviousCard]);

  return {
    slidePhase,
    slideDirection,
    transitionEnabled,
    animateToNextCard,
    animateToPreviousCard,
    animateNavigation
  };
};

export const StudyCardSlide = ({
  slidePhase,
  slideDirection,
  transitionEnabled,
  children
}: {
  slidePhase: SlidePhase;
  slideDirection: SlideDirection;
  transitionEnabled: boolean;
  children: ReactNode;
}) => {
  const classes = useStyles();
  const phaseClass =
    slidePhase === "exit"
      ? slideDirection === "forward"
        ? classes.exitLeft
        : classes.exitRight
      : slidePhase === "enter"
        ? slideDirection === "forward"
          ? classes.enterFromRight
          : classes.enterFromLeft
        : classes.idle;

  return (
    <div className={classes.stage}>
      <div
        className={`${classes.slide} ${phaseClass}`}
        style={{
          transition: transitionEnabled
            ? `transform ${CARD_SLIDE_MS}ms cubic-bezier(0.4, 0, 0.2, 1), opacity ${CARD_SLIDE_MS}ms ease`
            : "none"
        }}>
        {children}
      </div>
    </div>
  );
};

export default StudyCardSlide;
